import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import Dynamic from './HeroCarouselLayouts/Dynamic';

type Props = {
  data: {
    __typename: string;
    title?: string | null;
    layoutCollection?: {
      __typename?: string;
      items: ({
        id?: string | null;
        sys: {
          id: string;
        };
        __typename?: string;
      } | null)[];
    } | null;
  };
};

interface LayoutProps {
  __typename: string;
  id: string;
  sys: {
    id: string;
    type: string;
  };
  layout: {
    id: number;
    title: string;
    sys: {
      id: string;
    };
  };
  bentoBoxCollection: {
    items: {
      __typename: string;
      sys: {
        id: string;
        type: string;
      };
    }[];
  };
}

interface LayoutsProps {
  typename: string;
  data: LayoutProps;
}

interface LayoutItem {
  id?: string | null;
  sys: {
    id: string;
  };
  __typename?: string;
}

const SynCarousel = ({ data }: Props) => {
  const [fetchedLayouts, setFetchedLayouts] = useState<LayoutsProps[]>([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (fetchedLayouts.length === 0) return;

    const interval = setInterval(() => {
      const nextSlide = (currentSlide + 1) % fetchedLayouts.length;
      handleDotClick(nextSlide);
    }, 5000); // 5 seconds

    return () => clearInterval(interval);
  }, [currentSlide, fetchedLayouts]);

  useEffect(() => {
    const fetchLayouts = async () => {
      const layoutItems = (data?.layoutCollection?.items || []).filter(
        (item): item is LayoutItem => item !== null && !!item?.sys?.id
      );

      const responses = await Promise.all(
        layoutItems.map(async (layoutItem: LayoutItem) => {
          const id = layoutItem?.sys?.id;
          const typename = layoutItem?.__typename;

          if (!id || !typename) return null;

          if (typename === 'HeroLayoutCarousel') {
            const res = await fetch('https://graphql.contentful.com/content/v1/spaces/u0ns9p1dn2b1/environments/syn', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: 'Bearer 7WTIzcAbrIzpI6OMPEqnZiZdELbNQTJWNXTvXlKBJYw',
              },
              body: JSON.stringify({
                query: `
                  query GetHeroLayoutCarousel($id: String!) {
                    heroLayoutCarousel(id: $id) {
                      sys { id }
                      id
                      layout {
                        __typename
                        sys { id }
                        id
                        title
                      }
                      bentoBoxCollection {
                        items {
                          __typename
                          sys { id }
                        }
                      }
                    }
                  }
                `,
                variables: { id },
              }),
            });

            const json = await res.json();
            if (!json?.data?.heroLayoutCarousel) return null;

            return {
              typename,
              data: json.data.heroLayoutCarousel as LayoutProps,
            };
          }

          return null;
        })
      );

      const cleaned = responses.filter((r): r is LayoutsProps => r !== null);
      setFetchedLayouts(cleaned);
    };

    fetchLayouts();
  }, [data]);

  const handleDotClick = (index: number) => {
    if (containerRef.current) {
      const slideWidth = containerRef.current.offsetWidth;
      containerRef.current.scrollTo({
        left: index * slideWidth,
        behavior: 'smooth',
      });
      setCurrentSlide(index);
    }
  };

  const handleScroll = () => {
    if (containerRef.current) {
      const scrollLeft = containerRef.current.scrollLeft;
      const width = containerRef.current.offsetWidth;
      const index = Math.round(scrollLeft / width);
      setCurrentSlide(index);
    }
  };

  return (
    <div className="min-h-[70vh] w-full overflow-hidden pb-24">
      <div
        ref={containerRef}
        onScroll={handleScroll}
        style={{
          display: 'flex',
          overflowX: 'scroll',
          scrollSnapType: 'x mandatory',
          scrollBehavior: 'smooth',
          scrollbarWidth: 'none', // Firefox
          msOverflowStyle: 'none', // IE/Edge
        }}
        className="hide-scrollbar"
      >
        {fetchedLayouts.map((layout, index) => (
          <div
            key={index}
            style={{
              flex: '0 0 100%',
              scrollSnapAlign: 'start',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              borderRadius: '28px',
              boxSizing: 'border-box',
              minHeight: '70vh',
              background: layout.typename === 'HeroLayoutCarousel' ? '#f0f0f0' : '#ffdddd',
            }}
          >
            {layout.typename === 'HeroLayoutCarousel' ? (
              <Dynamic data={layout.data} />
            ) : (
              <p>Unknown layout type: {layout.typename}</p>
            )}
          </div>
        ))}
      </div>

      {/* Dots navigation */}
      {fetchedLayouts.length > 1 && (
        <div className="mx-auto flex w-full max-w-[936px] flex-col items-center justify-center rounded-full bg-[#E8E8E8] py-4">
          <div className="flex w-full gap-2 px-4">
            {fetchedLayouts.map((_, index) => (
              <span
                key={index}
                onClick={() => handleDotClick(index)}
                className={`h-[10px] flex-1 cursor-pointer rounded-full transition-colors ${
                  index === currentSlide ? 'bg-[var(--Schemes-Primary,#F14157)]' : 'bg-[rgba(44,45,48,0.12)]'
                }`}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SynCarousel;
